import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Minus,
  MoveHorizontal,
  MoveVertical,
  Plus,
  Printer,
  Scan,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { PDF_OPTIONS } from "./pdfjs-setup.js";

/* ─────────────────────────── types ─────────────────────────── */

export interface PdfViewerProps {
  /** Blob URL (local items) or short-lived signed URL (cloud items). */
  src: string;
  /** Used for the print window title and fallback download link. */
  filename: string;
  /**
   * Called when the user clicks the toolbar's print button. The host
   * route owns the print dialog so other media kinds can share it.
   * `currentPage` lets the host default the "Aktuelle Seite" option.
   */
  onPrint?: (args: { currentPage: number; totalPages: number }) => void;
  /**
   * Fires whenever the visible page or total page count changes. Hosts
   * that surface their own print UI outside the viewer use this to keep
   * the print dialog's "Aktuelle Seite" default in sync.
   */
  onInfoChange?: (info: { currentPage: number; totalPages: number }) => void;
  /**
   * Hide the viewer's built-in "Drucken" toolbar button — used when the
   * host renders its own print control elsewhere (e.g. a unified
   * lightbox header).
   */
  hidePrintButton?: boolean;
}

type FitMode = "width" | "height" | "page" | "custom";

/* ─────────────────────────── component ─────────────────────── */

export function PdfViewer({
  src,
  filename,
  onPrint,
  onInfoChange,
  hidePrintButton,
}: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [fit, setFit] = useState<FitMode>("page");
  const [scale, setScale] = useState<number>(1);
  const [container, setContainer] = useState<{ w: number; h: number }>({
    w: 0,
    h: 0,
  });
  // Natural page dimensions at scale=1 (captured from Page.onLoadSuccess).
  const [pageNatural, setPageNatural] = useState<{
    w: number;
    h: number;
  } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The stage div only mounts *after* the PDF document has loaded —
  // before then `<Document>` renders its loading element instead. A
  // ref+useEffect with empty deps would therefore never see the element
  // and the ResizeObserver would never attach (container stays at
  // {0,0} → every fit-mode degenerates to scale 1 = visually a no-op).
  // Using a callback ref re-runs the effect when the element appears.
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);

  // Track container size so fit modes recompute on resize / sidebar toggle.
  useEffect(() => {
    if (!stageEl) return;
    // Measure once immediately — ResizeObserver fires async, and the
    // first fit-calc on mount would otherwise see the initial {0,0}.
    setContainer({ w: stageEl.clientWidth, h: stageEl.clientHeight });
    const ro = new ResizeObserver(() => {
      setContainer({ w: stageEl.clientWidth, h: stageEl.clientHeight });
    });
    ro.observe(stageEl);
    return () => ro.disconnect();
  }, [stageEl]);

  // Reset on src change (new PDF).
  useEffect(() => {
    setPage(1);
    setNumPages(0);
    setLoadError(null);
    setFit("page");
    setScale(1);
    setPageNatural(null);
  }, [src]);

  // Derived scale for fit modes. Uses both axes so a landscape page
  // shrinks vertically to fit the viewport instead of overflowing.
  const PAD = 24; // breathing room around the page
  const fitScale = (() => {
    if (!pageNatural || container.w <= 0 || container.h <= 0) return 1;
    const availW = Math.max(120, container.w - PAD);
    const availH = Math.max(120, container.h - PAD);
    if (fit === "width") {
      // Pure width fit (page may be taller than viewport → user scrolls).
      return availW / pageNatural.w;
    }
    if (fit === "height") {
      // Pure height fit (page may be wider than viewport → user scrolls).
      return availH / pageNatural.h;
    }
    if (fit === "page") {
      // Whole page visible — clamp by the more constraining axis.
      return Math.min(availW / pageNatural.w, availH / pageNatural.h);
    }
    return 1;
  })();

  const effectiveScale = fit === "custom" ? scale : fitScale;

  function clampPage(next: number) {
    if (!numPages) return 1;
    return Math.max(1, Math.min(numPages, next));
  }

  function changePage(delta: number) {
    setPage((p) => clampPage(p + delta));
  }

  function zoom(delta: number) {
    // Hand off from a fit mode → seed custom scale at the current
    // effective scale so the user doesn't get a visual jump.
    setScale((prev) => {
      const base = fit === "custom" ? prev : fitScale || 1;
      return Math.max(0.4, Math.min(4, +(base + delta).toFixed(2)));
    });
    setFit("custom");
  }

  function fitWidth() {
    setFit("width");
  }

  function fitHeight() {
    setFit("height");
  }

  function fitPage() {
    setFit("page");
  }

  // Surface current / total page info to host controls (header Print btn).
  // Stash the callback in a ref so an unstable parent-supplied function
  // doesn't re-fire this effect on every render (→ infinite loop).
  const onInfoChangeRef = useRef(onInfoChange);
  useEffect(() => {
    onInfoChangeRef.current = onInfoChange;
  }, [onInfoChange]);
  useEffect(() => {
    if (numPages <= 0) return;
    onInfoChangeRef.current?.({ currentPage: page, totalPages: numPages });
  }, [page, numPages]);

  function printPdf() {
    if (onPrint) {
      onPrint({ currentPage: page, totalPages: numPages });
      return;
    }
    // Fallback if no host handler provided: native iframe print.
    const frame = document.createElement("iframe");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
    frame.src = src;
    frame.onload = () => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } catch {
        window.open(src, "_blank", "noopener,noreferrer");
      }
    };
    document.body.appendChild(frame);
    window.setTimeout(() => frame.remove(), 60_000);
  }

  return (
    <div
      className="flex h-full w-full flex-col rounded-2xl"
      style={{
        height: "calc(100vh - 7rem)",
        background: "rgba(20,24,22,0.55)",
        border: "1px solid rgba(255,255,255,0.08)",
        backdropFilter: "blur(10px)",
      }}
    >
      <Document
        file={src}
        options={PDF_OPTIONS}
        loading={
          <div className="flex h-full w-full flex-1 items-center justify-center self-stretch">
            <DocLoading />
          </div>
        }
        error={
          <div className="flex h-full w-full flex-1 items-center justify-center self-stretch">
            <DocError onRetry={() => setLoadError(null)} message={loadError} />
          </div>
        }
        onLoadSuccess={({ numPages }) => {
          setNumPages(numPages);
          setLoadError(null);
        }}
        onLoadError={(err) => setLoadError(err.message || "PDF konnte nicht geladen werden.")}
        className="flex min-h-0 flex-1 flex-row"
      >
        {/* Thumbnail sidebar */}
        <aside
          className="hidden shrink-0 overflow-y-auto border-r border-white/8 px-2 py-3 md:block"
          style={{ width: 132 }}
        >
          {numPages > 0
            ? Array.from({ length: numPages }, (_, i) => i + 1).map((n) => {
                const active = n === page;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setPage(n)}
                    className="mb-2 block w-full overflow-hidden rounded-lg border text-left transition"
                    style={{
                      borderColor: active ? "var(--color-pitch-lime)" : "rgba(255,255,255,0.08)",
                      background: active ? "rgba(178,255,89,0.06)" : "rgba(255,255,255,0.02)",
                    }}
                  >
                    <div className="flex items-center justify-center bg-white/95">
                      <Page
                        pageNumber={n}
                        width={108}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        loading={
                          <div className="flex h-32 w-full items-center justify-center text-white/40">
                            <Loader2 className="size-4 animate-spin" />
                          </div>
                        }
                      />
                    </div>
                    <div
                      className="px-2 py-1 text-center text-[11px] font-medium"
                      style={{
                        color: active ? "var(--color-pitch-lime)" : "rgba(255,255,255,0.6)",
                      }}
                    >
                      Seite {n}
                    </div>
                  </button>
                );
              })
            : null}
        </aside>

        {/* Main page viewport */}
        <div
          ref={setStageEl}
          className="flex min-h-0 flex-1 items-start justify-center overflow-auto p-4"
        >
          {numPages > 0 ? (
            <Page
              pageNumber={page}
              scale={effectiveScale}
              renderTextLayer
              renderAnnotationLayer
              className="shadow-2xl"
              onLoadSuccess={(pageProxy) => {
                // Capture natural dimensions (at scale=1) on first page so
                // fit-modes can compute the correct scale for the viewport.
                const vp = pageProxy.getViewport({ scale: 1 });
                setPageNatural((prev) => {
                  if (
                    prev &&
                    Math.abs(prev.w - vp.width) < 0.5 &&
                    Math.abs(prev.h - vp.height) < 0.5
                  ) {
                    return prev;
                  }
                  return { w: vp.width, h: vp.height };
                });
              }}
              loading={
                <div className="flex h-64 items-center justify-center text-white/60">
                  <Loader2 className="size-6 animate-spin" />
                </div>
              }
            />
          ) : null}
        </div>
      </Document>

      {/* Toolbar */}
      <div
        className="flex flex-wrap items-center justify-between gap-2 border-t border-white/8 px-3 py-2"
        style={{ background: "rgba(0,0,0,0.25)" }}
      >
        {/* Pagination cluster */}
        <div className="flex items-center gap-1">
          <ToolbarBtn label="Vorherige Seite" disabled={page <= 1} onClick={() => changePage(-1)}>
            <ChevronLeft className="size-4" />
          </ToolbarBtn>
          <div
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-semibold tabular-nums"
            style={{
              background: "rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.9)",
              minWidth: 76,
              justifyContent: "center",
            }}
          >
            <input
              type="number"
              value={page}
              min={1}
              max={numPages || 1}
              onChange={(e) => {
                const n = Number.parseInt(e.target.value, 10);
                if (Number.isFinite(n)) setPage(clampPage(n));
              }}
              className="w-10 bg-transparent text-center outline-none"
              style={{ MozAppearance: "textfield" } as React.CSSProperties}
            />
            <span style={{ color: "rgba(255,255,255,0.45)" }}>/</span>
            <span>{numPages || "–"}</span>
          </div>
          <ToolbarBtn
            label="Nächste Seite"
            disabled={!numPages || page >= numPages}
            onClick={() => changePage(1)}
          >
            <ChevronRight className="size-4" />
          </ToolbarBtn>
        </div>

        {/* Zoom cluster */}
        <div className="flex items-center gap-1">
          <ToolbarBtn label="Verkleinern" onClick={() => zoom(-0.1)}>
            <Minus className="size-4" />
          </ToolbarBtn>
          <div
            className="rounded-lg px-2 py-1 text-[12px] font-semibold tabular-nums"
            style={{
              background: "rgba(255,255,255,0.06)",
              color: "rgba(255,255,255,0.9)",
              minWidth: 64,
              textAlign: "center",
            }}
          >
            {fit === "width"
              ? "Breite"
              : fit === "height"
                ? "Höhe"
                : fit === "page"
                  ? "Seite"
                  : `${Math.round(scale * 100)}%`}
          </div>
          <ToolbarBtn label="Vergrößern" onClick={() => zoom(0.1)}>
            <Plus className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn label="An Breite anpassen" onClick={fitWidth} active={fit === "width"}>
            <MoveHorizontal className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn label="An Höhe anpassen" onClick={fitHeight} active={fit === "height"}>
            <MoveVertical className="size-4" />
          </ToolbarBtn>
          <ToolbarBtn label="Ganze Seite anzeigen" onClick={fitPage} active={fit === "page"}>
            <Scan className="size-4" />
          </ToolbarBtn>
        </div>

        {/* Actions cluster */}
        {!hidePrintButton && (
          <div className="flex items-center gap-1">
            <ToolbarBtn label="Drucken" onClick={printPdf}>
              <Printer className="size-4" />
              <span className="ml-1 hidden text-[12px] font-semibold sm:inline">Drucken</span>
            </ToolbarBtn>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── subcomponents ─────────────────────── */

function ToolbarBtn({
  children,
  onClick,
  label,
  disabled,
  active,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className="inline-flex items-center justify-center rounded-lg px-2 py-1.5 transition disabled:opacity-40"
      style={{
        background: active ? "rgba(178,255,89,0.14)" : "rgba(255,255,255,0.04)",
        border: active ? "1px solid rgba(178,255,89,0.45)" : "1px solid rgba(255,255,255,0.08)",
        color: active ? "var(--color-pitch-lime)" : "rgba(255,255,255,0.85)",
      }}
    >
      {children}
    </button>
  );
}

function DocLoading() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <Loader2 className="size-8 animate-spin text-white/80" />
    </div>
  );
}

function DocError({ message, onRetry }: { message: string | null; onRetry: () => void }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-[13px]" style={{ color: "var(--color-error, #ff6b6b)" }}>
        {message || "PDF konnte nicht geladen werden."}
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg px-3 py-1.5 text-[12px] font-semibold"
        style={{ background: "var(--color-pitch-lime)", color: "#1d3700" }}
      >
        Erneut versuchen
      </button>
    </div>
  );
}
